/**
 * Runtime layout-override store + fetch/save.
 *
 * Load order: empty → same-origin public/layout-overrides.json → remote API.
 * Network failure never blocks gameplay (falls back to bundled/empty).
 */

import {
  emptyLayoutOverrides,
  isLayoutOverridesDocument,
  mergeLayoutOverrides,
  type LayoutOverridesDocument,
} from '@/world-prototype/ownerEdit/layoutOverrideTypes';
import {
  getOwnerEditSessionToken,
  ownerLayoutApiBase,
} from '@/world-prototype/ownerEdit/auth';

const LOCAL_OVERRIDES_KEY = 'porchlight-layout-overrides';

let cache: LayoutOverridesDocument = emptyLayoutOverrides();
let bootstrapped = false;

export function getLayoutOverrides(): LayoutOverridesDocument {
  return cache;
}

export function setLayoutOverrides(doc: LayoutOverridesDocument): void {
  cache = doc;
}

function readLocalOverrides(): LayoutOverridesDocument | null {
  try {
    const raw = localStorage.getItem(LOCAL_OVERRIDES_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    return isLayoutOverridesDocument(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeLocalOverrides(doc: LayoutOverridesDocument): boolean {
  try {
    localStorage.setItem(LOCAL_OVERRIDES_KEY, JSON.stringify(doc));
    return true;
  } catch {
    return false;
  }
}

async function fetchJson(url: string): Promise<unknown | null> {
  try {
    const res = await fetch(url, { cache: 'no-store' });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

function resolvePublicOverridesUrl(): string {
  const base = import.meta.env.BASE_URL || '/';
  const root = base.endsWith('/') ? base : `${base}/`;
  return `${root}layout-overrides.json`;
}

/** Load bundled + local + remote overrides. Safe to call multiple times. */
export async function bootstrapLayoutOverrides(): Promise<{
  source: string;
  doc: LayoutOverridesDocument;
}> {
  let doc = emptyLayoutOverrides();
  const sources: string[] = [];

  const publicRaw = await fetchJson(resolvePublicOverridesUrl());
  if (isLayoutOverridesDocument(publicRaw)) {
    doc = mergeLayoutOverrides(doc, publicRaw);
    sources.push('public');
  }

  const api = ownerLayoutApiBase();
  if (api) {
    const remoteRaw = await fetchJson(`${api}/overrides`);
    if (isLayoutOverridesDocument(remoteRaw)) {
      doc = mergeLayoutOverrides(doc, remoteRaw);
      sources.push('remote');
      // Keep this device aligned with the shared store (don't let stale local win).
      writeLocalOverrides(doc);
    } else {
      const local = readLocalOverrides();
      if (local) {
        doc = mergeLayoutOverrides(doc, local);
        sources.push('local');
      }
    }
  } else {
    const local = readLocalOverrides();
    if (local) {
      doc = mergeLayoutOverrides(doc, local);
      sources.push('local');
    }
  }

  cache = doc;
  bootstrapped = true;
  return { source: sources.join('+') || 'empty', doc };
}

/**
 * Re-fetch remote overrides only.
 * Used when a play phone returns to the foreground after an editor Save.
 */
export async function refreshRemoteLayoutOverrides(): Promise<{
  changed: boolean;
  doc: LayoutOverridesDocument;
}> {
  const api = ownerLayoutApiBase();
  if (!api) {
    return { changed: false, doc: cache };
  }
  const before = JSON.stringify(cache);
  const remoteRaw = await fetchJson(`${api}/overrides`);
  if (!isLayoutOverridesDocument(remoteRaw)) {
    return { changed: false, doc: cache };
  }
  let doc = emptyLayoutOverrides();
  const publicRaw = await fetchJson(resolvePublicOverridesUrl());
  if (isLayoutOverridesDocument(publicRaw)) {
    doc = mergeLayoutOverrides(doc, publicRaw);
  }
  doc = mergeLayoutOverrides(doc, remoteRaw);
  writeLocalOverrides(doc);
  cache = doc;
  const changed = JSON.stringify(doc) !== before;
  return { changed, doc };
}

export function isLayoutOverridesBootstrapped(): boolean {
  return bootstrapped;
}

export type SaveOverridesResult =
  | { ok: true; mode: 'api' | 'dev-file' | 'local' }
  | { ok: false; error: string; downloaded?: boolean };

/**
 * Persist overrides. Prefers Owner Layout API; in DEV also writes
 * public/layout-overrides.json; always mirrors to localStorage so prod
 * edits stick on this device without a Worker.
 */
export async function saveLayoutOverridesDocument(
  patch: LayoutOverridesDocument,
): Promise<SaveOverridesResult> {
  const next = mergeLayoutOverrides(cache, {
    ...patch,
    updatedAt: new Date().toISOString(),
  });
  cache = next;

  const api = ownerLayoutApiBase();
  const token = getOwnerEditSessionToken();
  if (api && token) {
    try {
      const res = await fetch(`${api}/overrides`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(next),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        writeLocalOverrides(next);
        return {
          ok: false,
          error: `Remote save failed (${res.status})${text ? `: ${text}` : ''}`,
        };
      }
      writeLocalOverrides(next);
      return { ok: true, mode: 'api' };
    } catch {
      writeLocalOverrides(next);
      return { ok: false, error: 'Remote save network error' };
    }
  }

  if (import.meta.env.DEV) {
    try {
      const res = await fetch('/__world-editor/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          path: 'public/layout-overrides.json',
          contents: `${JSON.stringify(next, null, 2)}\n`,
        }),
      });
      if (res.ok) {
        writeLocalOverrides(next);
        return { ok: true, mode: 'dev-file' };
      }
    } catch {
      /* fall through */
    }
  }

  if (writeLocalOverrides(next)) {
    return { ok: true, mode: 'local' };
  }

  return {
    ok: false,
    error: 'Could not save layout overrides on this device.',
  };
}
